import * as crypto from "node:crypto";
import * as fs from "node:fs";
import * as path from "node:path";

import {
  CliError,
  EXIT_INPUT,
  EXIT_OK,
  EXIT_USAGE,
  scriptJsonPath,
  sha256Text,
  type CommandOutput,
} from "../../common.js";
import { applyAuthoring, type AuthoringApplyResult } from "../../domain/authoring/v3/apply.js";
import { parseAuthoring } from "../../domain/authoring/v3/parser.js";
import type { AuthoringMode } from "../../domain/authoring/v3/types.js";
import { requireScriptV3 } from "../../domain/script/v3/parse.js";
import type { ScriptV3 } from "../../domain/script/v3/types.js";
import {
  requireStructurallyReadableScriptDocument,
  type ScriptDocument,
} from "../../domain/script/dispatch.js";
import { requireStructurallyReadableScriptV4 } from "../../domain/script/v4/parse.js";
import { parseAuthoringV4 } from "../../domain/authoring/v4/parser.js";
import {
  applyAuthoringV4,
  type V4AuthoringApplyResult,
} from "../../domain/authoring/v4/apply.js";
import {
  assertLocalFileFingerprint,
  assertScriptUnchanged,
  atomicWriteJson,
  localFileFingerprint,
  semanticScriptHash,
} from "../../infra/script-working-copy.js";
import { strOf, type Dict } from "../../domain/script/shared.js";

interface AuthoringPlanIdentity {
  version: 1;
  sourceDigest: string;
  mode: AuthoringMode;
  targetKind: string;
  targets: string[];
  baseline: string;
  plan:
    | Pick<AuthoringApplyResult, "targets" | "declarations" | "retainedOrphanAssets" | "validation">
    | Pick<V4AuthoringApplyResult, "targets" | "declarations" | "retainedOrphanAssets" | "identity" | "validation">;
}

function usage(message: string, received: string[]): never {
  throw new CliError(message, {
    exitCode: EXIT_USAGE,
    errorCode: "AUTHORING_USAGE_INVALID",
    hint: "Choose exactly one mode and pass a regular authoring source file.",
    data: {
      expected: ["scriptctl authoring write <file> (--create|--replace) [--apply --plan <digest>]"],
      actual: received,
    },
  });
}

function readSource(rawPath: string): { absolutePath: string; bytes: Buffer; text: string } {
  if (!rawPath || rawPath === "-") usage("Authoring input must be a named file.", [rawPath || "<missing>"]);
  const absolutePath = path.resolve(rawPath);
  let stat: fs.Stats;
  try {
    stat = fs.statSync(absolutePath);
  } catch {
    throw new CliError("The authoring source file does not exist.", {
            exitCode: EXIT_INPUT,
            errorCode: "AUTHORING_SOURCE_UNAVAILABLE",
            hint: "Write the complete authoring draft to a file, then rerun the command.",
            data: { actual: [absolutePath] },
          });
  }
  if (!stat.isFile()) usage("Authoring input must be a regular file.", [absolutePath]);
  const bytes = fs.readFileSync(absolutePath);
  return { absolutePath, bytes, text: bytes.toString("utf-8") };
}

function readCurrent(scriptPath: string): {
  script: ScriptDocument | null;
  baseline: string;
  fingerprint: string | null;
} {
  const fingerprint = localFileFingerprint(scriptPath);
  if (fingerprint === null) return { script: null, baseline: "missing", fingerprint };
  let parsed: unknown;
  try {
    parsed = JSON.parse(fs.readFileSync(scriptPath, "utf-8"));
  } catch (error) {
    throw new CliError("The local script is not valid JSON.", {
            exitCode: EXIT_INPUT,
            errorCode: "SCRIPT_JSON_INVALID",
            hint: "Repair the local JSON file, then rerun authoring.",
            data: { actual: [scriptPath, error instanceof Error ? error.message : String(error)] },
          });
  }
  const script = requireStructurallyReadableScriptDocument(parsed);
  return { script, baseline: semanticScriptHash(script), fingerprint };
}

function selectedMode(opts: Dict): AuthoringMode {
  const create = opts["create"] === true;
  const replace = opts["replace"] === true;
  if (create === replace) usage("Choose exactly one authoring mode.", [
    `create=${create}`,
    `replace=${replace}`,
  ]);
  return create ? "create" : "replace";
}

function planDigest(identity: AuthoringPlanIdentity): string {
  return `sha256:${sha256Text(JSON.stringify(identity))}`;
}

function totals(result: AuthoringApplyResult): {
  before: { episodes: number; scenes: number; actions: number };
  after: { episodes: number; scenes: number; actions: number };
} {
  return result.targets.reduce(
    (sum, target) => ({
      before: {
        episodes: sum.before.episodes + target.before.episodes,
        scenes: sum.before.scenes + target.before.scenes,
        actions: sum.before.actions + target.before.actions,
      },
      after: {
        episodes: sum.after.episodes + target.after.episodes,
        scenes: sum.after.scenes + target.after.scenes,
        actions: sum.after.actions + target.after.actions,
      },
    }),
    {
      before: { episodes: 0, scenes: 0, actions: 0 },
      after: { episodes: 0, scenes: 0, actions: 0 },
    },
  );
}

function targetResultLines(result: AuthoringApplyResult): string[] {
  const lines = result.targets.map((target, index) => [
    `target[${index}]=${target.address}`,
    `kind=${target.kind}`,
    `before=${JSON.stringify(target.before)}`,
    `after=${JSON.stringify(target.after)}`,
    `retained=${JSON.stringify(target.retainedIds)}`,
    `created=${JSON.stringify(target.createdIds)}`,
    `removed=${JSON.stringify(target.removedIds)}`,
    `untouched_siblings=${target.untouchedSiblings}`,
  ].join(" "));
  lines.push(
    `created_assets=${JSON.stringify(result.declarations.createdAssets)}`,
    `existing_assets=${JSON.stringify(result.declarations.existingAssets)}`,
    `created_states=${JSON.stringify(result.declarations.createdStates)}`,
    `existing_states=${JSON.stringify(result.declarations.existingStates)}`,
    `retained_orphan_assets=${JSON.stringify(result.retainedOrphanAssets)}`,
    `validation_ok=${result.validation.ok} errors=${result.validation.errors.length} warnings=${result.validation.warnings.length}`,
  );
  for (const issue of result.validation.errors) {
    lines.push(`validation_error=${issue.path} ${issue.code} ${issue.message}`);
  }
  for (const issue of result.validation.warnings) {
    lines.push(`validation_warning=${issue.path} ${issue.code} ${issue.message}`);
  }
  return lines;
}

function commandWriteAuthoringV4(
  opts: Dict,
  source: { absolutePath: string; bytes: Buffer; text: string },
  mode: AuthoringMode,
  scriptPath: string,
  current: ReturnType<typeof readCurrent>,
): [CommandOutput, number] {
  const document = parseAuthoringV4(source.text);
  const currentV4 = current.script === null
    ? null
    : requireStructurallyReadableScriptV4(current.script);
  const proposed = applyAuthoringV4(currentV4, document, mode);
  const sourceDigest = `sha256:${crypto.createHash("sha256").update(source.bytes).digest("hex")}`;
  const identity: AuthoringPlanIdentity = {
    version: 1,
    sourceDigest,
    mode,
    targetKind: document.targetKind,
    targets: proposed.targets.map((target) => target.address),
    baseline: current.baseline,
    plan: {
      targets: proposed.targets,
      declarations: proposed.declarations,
      retainedOrphanAssets: proposed.retainedOrphanAssets,
      identity: proposed.identity,
      validation: proposed.validation,
    },
  };
  const digest = planDigest(identity);
  const apply = opts["apply"] === true;
  const suppliedPlan = strOf(opts["plan"]).trim();
  if (apply && !suppliedPlan) usage("--apply requires the exact digest returned by the current plan.", ["plan=<missing>"]);
  if (!apply && suppliedPlan) usage("--plan is only valid together with --apply.", [`plan=${suppliedPlan}`]);
  if (apply && suppliedPlan !== digest) {
    throw new CliError("The supplied plan does not match the current source, mode, targets, or script baseline.", {
            exitCode: EXIT_INPUT,
            errorCode: "AUTHORING_PLAN_MISMATCH",
            hint: "Review the newly computed plan, then apply its exact digest.",
            data: { expected: [digest], actual: [suppliedPlan] },
          });
  }
  if (apply && proposed.changed) {
    if (current.script === null) assertLocalFileFingerprint(scriptPath, current.fingerprint);
    else assertScriptUnchanged(scriptPath, current.baseline);
    atomicWriteJson(scriptPath, proposed.script);
  }
  const resultLines = proposed.targets.map((target, index) =>
    `target[${index}]=${target.address} kind=${target.kind} before=${JSON.stringify(target.before)} after=${JSON.stringify(target.after)} retained=${JSON.stringify(target.retainedIds)} created=${JSON.stringify(target.createdIds)} removed=${JSON.stringify(target.removedIds)} untouched_siblings=${target.untouchedSiblings}`);
  resultLines.push(
    `created_assets=${JSON.stringify(proposed.declarations.createdAssets)}`,
    `existing_assets=${JSON.stringify(proposed.declarations.existingAssets)}`,
    `created_states=${JSON.stringify(proposed.declarations.createdStates)}`,
    `existing_states=${JSON.stringify(proposed.declarations.existingStates)}`,
    `retained_orphan_assets=${JSON.stringify(proposed.retainedOrphanAssets)}`,
    `created_ids=${JSON.stringify(proposed.identity.createdIds)}`,
    `removed_ids=${JSON.stringify(proposed.identity.removedIds)}`,
    `retained_ids=${JSON.stringify(proposed.identity.retainedIds)}`,
  );
  const summary = `${apply ? "Applied" : "Planned"} ${proposed.targets.length} ${document.targetKind} target(s); validation ${proposed.validation.ok ? "passed" : "needs repair"}`;
  const report: CommandOutput = {
    text: [summary, `plan: ${digest}`, ...resultLines].join("\n"),
    data: {
      op: "write",
      changed: proposed.changed,
      source_path: source.absolutePath,
      source_digest: sourceDigest,
      script_path: scriptPath,
      baseline: current.baseline,
      script_hash: current.baseline === "missing" ? null : current.baseline,
      plan_digest: digest,
      plan: identity.plan,
      mode,
      target_kind: document.targetKind,
      targets: proposed.targets,
      declarations: proposed.declarations,
      retained_orphan_assets: proposed.retainedOrphanAssets,
      identity: proposed.identity,
      validation: proposed.validation,
      applied: apply,
      script_version: 4,
    },
  };
  return [report, EXIT_OK];
}

export function commandWriteAuthoring(opts: Dict): [CommandOutput, number] {
  const args = Array.isArray(opts["_args"]) ? opts["_args"] : [];
  const sourceArg = typeof args[0] === "string" ? args[0] : "";
  const source = readSource(sourceArg);
  const mode = selectedMode(opts);
  const scriptPath = scriptJsonPath(opts);
  const current = readCurrent(scriptPath);
  if (current.script === null || current.script.version === 4) {
    return commandWriteAuthoringV4(opts, source, mode, scriptPath, current);
  }
  const document = parseAuthoring(source.text);
  const proposed = applyAuthoring(requireScriptV3(current.script), document, mode);
  const sourceDigest = `sha256:${crypto.createHash("sha256").update(source.bytes).digest("hex")}`;
  const identity: AuthoringPlanIdentity = {
    version: 1,
    sourceDigest,
    mode,
    targetKind: document.targetKind,
    targets: proposed.targets.map((target) => target.address),
    baseline: current.baseline,
    plan: {
      targets: proposed.targets,
      declarations: proposed.declarations,
      retainedOrphanAssets: proposed.retainedOrphanAssets,
      validation: proposed.validation,
    },
  };
  const digest = planDigest(identity);
  const apply = opts["apply"] === true;
  const suppliedPlan = strOf(opts["plan"]).trim();
  if (apply && !suppliedPlan) usage("--apply requires the exact digest returned by the current plan.", ["plan=<missing>"]);
  if (!apply && suppliedPlan) usage("--plan is only valid together with --apply.", [`plan=${suppliedPlan}`]);
  if (apply && suppliedPlan !== digest) {
    throw new CliError("The supplied plan does not match the current source, mode, targets, or script baseline.", {
            exitCode: EXIT_INPUT,
            errorCode: "AUTHORING_PLAN_MISMATCH",
            hint: "Review the newly computed plan, then apply its exact digest.",
            data: { expected: [digest], actual: [suppliedPlan] },
          });
  }

  if (apply && proposed.changed) {
    if (current.script === null) assertLocalFileFingerprint(scriptPath, current.fingerprint);
    else assertScriptUnchanged(scriptPath, current.baseline);
    atomicWriteJson(scriptPath, proposed.script);
  }

  const counts = totals(proposed);
  const summary = `${apply ? "Applied" : "Planned"} ${proposed.targets.length} ${document.targetKind} target(s); episodes ${counts.before.episodes}→${counts.after.episodes}, scenes ${counts.before.scenes}→${counts.after.scenes}, actions ${counts.before.actions}→${counts.after.actions}`;
  const report: CommandOutput = {
    text: [summary, `plan: ${digest}`, ...targetResultLines(proposed)].join("\n"),
    data: {
      op: "write",
      changed: proposed.changed,
      source_path: source.absolutePath,
      source_digest: sourceDigest,
      script_path: scriptPath,
      baseline: current.baseline,
      script_hash: current.baseline === "missing" ? null : current.baseline,
      plan_digest: digest,
      plan: identity.plan,
      mode,
      target_kind: document.targetKind,
      targets: proposed.targets,
      declarations: proposed.declarations,
      counts,
      validation: proposed.validation,
      retained_orphan_assets: proposed.retainedOrphanAssets,
      applied: apply,
      script_version: 3,
    },
  };
  return [report, EXIT_OK];
}
